import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, UserCheck, UserX, Clock, TrendingUp, BarChart3, Target, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  'New': '#3b82f6',
  'Waiting to Schedule 1st Interview': '#8b5cf6',
  '1st Interview Scheduled': '#a855f7',
  '1st Interview Complete': '#7c3aed',
  'Waiting to Schedule 2nd Interview': '#6d28d9',
  '2nd Interview Scheduled': '#5b21b6',
  '2nd Interview Complete': '#4c1d95',
  'Background Check': '#f59e0b',
  'Offer Extended': '#f97316',
  'Offer Accepted': '#22c55e',
  'Hired': '#16a34a',
};

export default function ApplicantPipeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ats/applicants/pipeline-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        console.error('Pipeline analytics error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading pipeline analytics...</div>;
  if (!data) return <div className="p-12 text-center text-muted-foreground">No data available</div>;

  const { summary, funnel, sources, positions, monthly_trend } = data;
  const maxFunnel = Math.max(...funnel.map(f => f.count), 1);
  const maxTrend = Math.max(...monthly_trend.map(m => m.count), 1);
  const maxSource = Math.max(...sources.map(s => s.total), 1);

  return (
    <div className="space-y-6" data-testid="applicant-pipeline-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4 text-center">
          <Users size={22} className="mx-auto mb-1 text-primary" />
          <p className="text-2xl font-bold">{summary.total}</p>
          <p className="text-xs text-muted-foreground">Total Applicants</p>
        </Card>
        <Card className="p-4 text-center bg-blue-50 border-blue-200">
          <Target size={22} className="mx-auto mb-1 text-blue-600" />
          <p className="text-2xl font-bold text-blue-600">{summary.active}</p>
          <p className="text-xs text-muted-foreground">Active in Pipeline</p>
        </Card>
        <Card className="p-4 text-center bg-green-50 border-green-200">
          <UserCheck size={22} className="mx-auto mb-1 text-green-600" />
          <p className="text-2xl font-bold text-green-600">{summary.hired}</p>
          <p className="text-xs text-muted-foreground">Hired</p>
        </Card>
        <Card className="p-4 text-center bg-gray-50 border-gray-200">
          <UserX size={22} className="mx-auto mb-1 text-gray-500" />
          <p className="text-2xl font-bold text-gray-500">{summary.inactive}</p>
          <p className="text-xs text-muted-foreground">Inactive/Lost</p>
        </Card>
        <Card className="p-4 text-center bg-amber-50 border-amber-200">
          <Clock size={22} className="mx-auto mb-1 text-amber-600" />
          <p className="text-2xl font-bold text-amber-600">{summary.avg_time_to_hire_days ?? '—'}</p>
          <p className="text-xs text-muted-foreground">Avg Days to Hire</p>
        </Card>
      </div>

      {/* Pipeline Funnel */}
      <Card className="p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 size={18} /> Pipeline Funnel</h3>
        <div className="space-y-2">
          {funnel.map((stage, i) => {
            const pct = maxFunnel > 0 ? (stage.count / maxFunnel) * 100 : 0;
            const color = STAGE_COLORS[stage.stage] || '#6b7280';
            return (
              <div key={i} className="flex items-center gap-3" data-testid={`funnel-stage-${i}`}>
                <span className="text-xs w-56 text-right truncate text-muted-foreground">{stage.stage}</span>
                <div className="flex-1 bg-muted rounded h-7 relative overflow-hidden">
                  <div className="h-full rounded transition-all flex items-center"
                    style={{ width: `${Math.max(pct, stage.count > 0 ? 3 : 0)}%`, background: color }}>
                    {stage.count > 0 && (
                      <span className="text-white text-xs font-bold ml-2">{stage.count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Effectiveness */}
        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} /> Source Effectiveness</h3>
          <div className="space-y-3">
            {sources.map((s, i) => (
              <div key={i} data-testid={`source-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{s.source}</span>
                  <div className="flex items-center gap-2">
                    <Badge className="text-[10px] bg-muted">{s.total} total</Badge>
                    {s.hired > 0 && <Badge className="text-[10px] bg-green-100 text-green-700">{s.hired} hired</Badge>}
                    {s.hire_rate > 0 && <span className="text-xs text-green-600 font-medium">{s.hire_rate}%</span>}
                  </div>
                </div>
                <div className="w-full bg-muted rounded h-4 flex overflow-hidden">
                  {s.active > 0 && <div style={{ width: `${(s.active/s.total)*100}%` }} className="bg-blue-400 h-full" title={`Active: ${s.active}`} />}
                  {s.hired > 0 && <div style={{ width: `${(s.hired/s.total)*100}%` }} className="bg-green-500 h-full" title={`Hired: ${s.hired}`} />}
                  {s.inactive > 0 && <div style={{ width: `${(s.inactive/s.total)*100}%` }} className="bg-gray-300 h-full" title={`Inactive: ${s.inactive}`} />}
                </div>
              </div>
            ))}
            <div className="flex gap-4 mt-2 justify-center text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400" /> Active</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> Hired</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300" /> Inactive</span>
            </div>
          </div>
        </Card>

        {/* Position Breakdown */}
        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Briefcase size={18} /> Position Breakdown</h3>
          {positions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No position data</p>
          ) : (
            <div className="space-y-3">
              {positions.map((p, i) => {
                const maxPos = Math.max(...positions.map(x => x.count), 1);
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`position-${i}`}>
                    <span className="text-sm font-medium w-24 truncate">{p.position}</span>
                    <div className="flex-1 bg-muted rounded h-6 overflow-hidden">
                      <div className="h-full rounded bg-primary/70 flex items-center transition-all"
                        style={{ width: `${(p.count / maxPos) * 100}%`, minWidth: p.count > 0 ? '24px' : 0 }}>
                        <span className="text-white text-xs font-bold ml-2">{p.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Monthly Application Trend */}
      <Card className="p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} /> Application Trend (Last 6 Months)</h3>
        <div className="flex items-end gap-2 h-36">
          {monthly_trend.map((m, i) => {
            const h = maxTrend > 0 ? (m.count / maxTrend) * 100 : 2;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-xs font-medium mb-1">{m.count > 0 ? m.count : ''}</span>
                <div className="w-full rounded-t bg-primary/80 hover:bg-primary transition-all"
                  style={{ height: `${Math.max(h, m.count > 0 ? 4 : 2)}%`, minHeight: '4px' }} />
                <span className="text-[10px] text-muted-foreground mt-1">{m.month.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
